import styled from 'styled-components'

import { Devices, Spacings, Text } from '@styles'

export const SearchWrapper = styled.div`
	display: flex;
	justify-content: end;
`

/* ─────────────────────────────────────────────────────────────────────────
   Wrappers shared across the Tenderbook routes (user list + user detail tabs).
   One-off styles live in the feature folder that uses them.
   ───────────────────────────────────────────────────────────────────────── */

/* ─── Page / tab layout ────────────────────────────────────────────────── */

// Wraps a routed tab's content; adds breathing room below the top tab bar.
export const PageContent = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	margin-top: ${Spacings.SPACING_3B};
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};

	/* Force every descendant (incl. buttons/inputs that reset it) onto Inter. */
	* {
		font-family: inherit;
	}

	@media ${Devices.tablet} {
		margin-top: ${Spacings.SPACING_4B};
	}
`

// Top row holding the main tabs on the left and contextual actions on the right.
export const TabBarRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
	width: 100%;
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};

	* {
		font-family: inherit;
	}
`

export const RightActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	margin-left: auto;
`

/* ─── Page level table wrappers ────────────────────────────────────────── */

export const PageLevelTableWrapper = styled.section`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;

	@media ${Devices.tablet} {
		padding-bottom: ${Spacings.SPACING_4B};
	}
`

export const FullHeightPageLevelTableWrapper = styled(PageLevelTableWrapper)`
	height: 100%;
`

/* ─── Section cards ────────────────────────────────────────────────────── */

export const SectionCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B};
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	box-shadow: ${({ theme }) => theme.shadows.sm};

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_5B};
	}
`

// Header row of a section: icon + title on the left, actions on the right.
export const SectionHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	width: 100%;
`

export const SectionTitle = styled.h3`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	margin: 0;
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};

	svg {
		font-size: ${Text.X_LARGE};
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

// Responsive card grid: 1 column on mobile, N (default 2) on tablet+.
export const CardGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${Spacings.SPACING_4B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 2}, 1fr);
	}
`

/* ─── Label / value field grid (detail cards) ──────────────────────────── */

export const FieldGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: ${Spacings.SPACING_4B} ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 3}, 1fr);
	}
`

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	min-width: 0;
`

export const FieldLabel = styled.span`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.BASE_SMALL};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const FieldValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
	word-break: break-word;
`

export const NoDataText = styled.p`
	margin: 0;
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
`

/* ─── Read-only info field grid (presentational detail cards) ──────────── */

// Always wraps, fixed-width cells (label above value). Pairs with the v2 Card compound
// components for read-only detail cards; use FieldGrid above when a strict column count
// is wanted instead of flow-wrapping.
export const InfoFieldsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: ${Spacings.SPACING_5B} ${Spacings.SPACING_5B};
`

export const InfoField = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	flex: 1 1 ${({ $basis }) => $basis || '160px'};
	min-width: ${({ $basis }) => $basis || '160px'};

	@media ${Devices.laptop} {
		flex: 0 1 ${({ $basis }) => $basis || '220px'};
	}
`

export const InfoFieldLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.REGULAR};
	color: ${({ theme }) => theme.colors.text.secondary};
	letter-spacing: 0.012em;
`

export const InfoFieldValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme, $variant }) => {
		if ($variant === 'link') return theme.colors.brand.primary
		if ($variant === 'success') return theme.colors.status.success.text
		return theme.colors.text.primary
	}};
	overflow-wrap: break-word;
`
